import axios from 'axios';
import apiClient from '../config/axiosConfig';
import { API_BASE_URL } from '../utils/apiUtils';
import { isAuthenticated } from '../utils/authUtils';

const validateLinkId = (linkId) => {
  if (!linkId || typeof linkId !== 'string' || linkId.trim() === '') {
    throw new Error('Link ID is required and must be a valid string');
  }
  return linkId.trim();
};

const validateUserId = (userId) => {
  if (userId && (typeof userId !== 'string' || userId.trim() === '')) {
    throw new Error('User ID must be a valid string');
  }
  return userId?.trim();
};

const validateCardId = (cardId) => {
  if (cardId && (typeof cardId !== 'string' || cardId.trim() === '')) {
    throw new Error('Card ID must be a valid string');
  }
  return cardId?.trim();
};

const validateTempCardId = (tempCardId) => {
  if (!tempCardId || typeof tempCardId !== 'string' || tempCardId.trim() === '') {
    throw new Error('Temporary card ID is required and must be a valid string');
  }
  return tempCardId.trim();
};

const validateContactLinkType = (type) => {
  if (!type || typeof type !== 'string' || type.trim() === '') {
    throw new Error('Contact link type is required and must be a valid string');
  }
  return type.trim();
};

const validateContactLinkDetails = (details) => {
  if (!details || typeof details !== 'object') {
    throw new Error('Contact link details must be a valid object');
  }
  return details;
};

const validateBooleanValue = (value, fieldName) => {
  if (value !== null && value !== undefined && typeof value !== 'boolean') {
    throw new Error(`${fieldName} must be a boolean`);
  }
  return value;
};

const validateStringValue = (value, fieldName) => {
  if (value !== null && value !== undefined && typeof value !== 'string') {
    throw new Error(`${fieldName} must be a string`);
  }
  return value;
};

const validateObjectValue = (value, fieldName) => {
  if (value && typeof value !== 'object') {
    throw new Error(`${fieldName} must be a valid object`);
  }
  return value;
};

const isInSignupFlow = () => {
  return localStorage.getItem('signupInProgress') === 'true';
};

const handleContactLinkAuthError = (error, operation = 'API operation') => {
  if (error.response?.status === 401) {
    localStorage.removeItem('authToken');
    localStorage.removeItem('authUser');
    throw new Error('Your session has expired. Please log in again');
  }

  if (error.response?.status === 403) {
    throw new Error('You do not have permission to access this contact link');
  }

  if (error.response?.status === 404) {
    throw new Error('Contact link not found or no longer available');
  }

  if (error.response?.status === 409) {
    throw new Error('Contact link already exists or conflicts with existing data');
  }

  if (error.response?.status === 422) {
    const validationErrors = error.response?.data?.errors || [];
    const errorMessage = validationErrors.length > 0
      ? `Validation errors: ${validationErrors.join(', ')}`
      : 'Invalid contact link data provided';
    throw new Error(errorMessage);
  }

  if (error.response?.status >= 500) {
    throw new Error('Server error. Please try again later.');
  }

  if (error.code === 'ERR_NETWORK') {
    throw new Error('Connection issue. Please check your internet and try again.');
  }

  if (error.code === 'ECONNABORTED') {
    throw new Error('The request took too long. Please try again.');
  }

  const errorMessage = error.response?.data?.message || error.message || 'Something went wrong. Please try again.';
  throw new Error(errorMessage);
};

export const createContactLink = async ({ userId = null, cardId = null, type, details, isHighlighted = false, isTemporaryCard = false }) => {
  try {
    if (isInSignupFlow()) {
      const mockResponse = {
        success: true,
        contactLink: {
          _id: `temp_${Date.now()}_${Math.random()}`,
          type: type,
          details: details,
          isHighlighted: isHighlighted || false,
          cardId: cardId,
          userId: userId,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        },
        message: 'Contact link created in context (signup flow)'
      };
      return mockResponse;
    }

    const validUserId = validateUserId(userId);
    const validCardId = validateCardId(cardId);
    const validType = validateContactLinkType(type);
    const validDetails = validateContactLinkDetails(details);
    const validIsHighlighted = validateBooleanValue(isHighlighted, 'isHighlighted');

    const contactLinkData = {
      type: validType,
      details: validDetails,
      isHighlighted: validIsHighlighted,
      userId: validUserId,
      cardId: validCardId
    };

    const endpoint = `${API_BASE_URL}/api/contact-link`;

    const response = await apiClient.post(endpoint, contactLinkData, {
      timeout: 30000
    });

    if (response.status === 201) {
      return response.data;
    }

    throw new Error('Failed to create contact link - unexpected status code');

  } catch (error) {
    if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
      throw new Error('Request timeout. Please check your connection and try again.');
    }

    if (error.code === 'ERR_NETWORK' || error.message.includes('Network Error')) {
      throw new Error('Network errors. Please check your connection and try again.');
    }

    if (error.response?.status === 400) {
      throw new Error(error.response?.data?.message || 'Invalid data provided. Please check your input and try again.');
    }

    handleContactLinkAuthError(error, 'Creating contact link');
  }
};

export const associateContactLinkWithUser = async (linkId, userId) => {
  try {
    const validLinkId = validateLinkId(linkId);
    const validUserId = validateUserId(userId);

    const response = await apiClient.put(`${API_BASE_URL}/api/contact-link/${validLinkId}`, {
      userId: validUserId,
      isTemporaryCard: false
    });

    if (response.status === 200) {
      return response.data;
    }
    throw new Error('Failed to associate contact link with user');
  } catch (error) {
    handleContactLinkAuthError(error, 'Associating contact link with user');
  }
};

export const associateContactLinkWithCard = async (linkId, cardId) => {
  try {
    const validLinkId = validateLinkId(linkId);
    const validCardId = validateCardId(cardId);

    const response = await apiClient.put(`${API_BASE_URL}/api/contact-link/${validLinkId}`, {
      cardId: validCardId
    });

    if (response.status === 200) {
      return response.data;
    }
    throw new Error('Failed to associate contact link with card');
  } catch (error) {
    handleContactLinkAuthError(error, 'Associating contact link with card');
  }
};

export const updateContactLink = async (linkId, updateData) => {
  try {
    const validLinkId = validateLinkId(linkId);
    const validUpdateData = validateObjectValue(updateData, 'Update data');

    try {
      const existingLink = await getContactLinkById(validLinkId);

      if (validUpdateData.details && existingLink?.contactLink?.details) {
        validUpdateData.details = {
          ...existingLink.contactLink.details,
          ...validUpdateData.details
        };
      }
    } catch (error) {
      // Proceed with update data as-is if existing link cannot be fetched
    }

    const response = await apiClient.put(`${API_BASE_URL}/api/contact-link/${validLinkId}`, validUpdateData);

    if (response.status === 200) {
      return response.data;
    }
    throw new Error('Failed to update contact link');
  } catch (error) {
    handleContactLinkAuthError(error, 'Updating contact link');
  }
};

export const updateContactLinkIcon = async (linkId, iconUrl) => {
  try {
    const validLinkId = validateLinkId(linkId);
    const validIconUrl = validateStringValue(iconUrl, 'Icon URL');

    const updateData = {
      details: {
        icon: validIconUrl
      }
    };

    const response = await apiClient.put(`${API_BASE_URL}/api/contact-link/${validLinkId}`, updateData);

    if (response.status === 200) {
      return response.data;
    }
    throw new Error('Failed to update contact link icon');
  } catch (error) {
    handleContactLinkAuthError(error, 'Updating contact link icon');
  }
};

export const getContactLinksByUser = async (userId) => {
  try {
    const validUserId = validateUserId(userId);

    const response = await apiClient.get(`${API_BASE_URL}/api/contact-link/user/${validUserId}`);

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to retrieve contact links with an unexpected status code');
    }
  } catch (error) {
    handleContactLinkAuthError(error, 'Getting contact links by user');
  }
};

export const getContactLinksByCard = async (cardId) => {
  try {
    const validCardId = validateCardId(cardId);

    const isUserAuthenticated = isAuthenticated();
    const endpoint = isUserAuthenticated
      ? `${API_BASE_URL}/api/contact-link/card/${validCardId}`
      : `${API_BASE_URL}/api/contact-link/public/card/${validCardId}`;

    const response = await apiClient.get(endpoint);

    if (response.status === 200) {
      return response.data;
    }

    throw new Error('Failed to retrieve contact links');
  } catch (error) {
    handleContactLinkAuthError(error, 'Getting contact links by card');
  }
};

export const deleteContactLink = async (id) => {
  try {
    const validId = validateLinkId(id);

    const response = await apiClient.delete(`${API_BASE_URL}/api/contact-link/${validId}`);

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to delete contact link with an unexpected status code');
    }
  } catch (error) {
    handleContactLinkAuthError(error, 'Deleting contact link');
  }
};

export const getContactLinkById = async (id) => {
  try {
    const validId = validateLinkId(id);

    const response = await apiClient.get(`${API_BASE_URL}/api/contact-link/${validId}`);

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to retrieve contact link with an unexpected status code');
    }
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to retrieve contact link by ID');
  }
};